/**
 * Write buffer for batching write operations
 */
import { getLogger } from '@qwickapps/logging';
import { serializeDates } from '../utils/serialization.js';
const log = getLogger('QwickForge.MiddlewareClient.WriteBuffer');
export class WriteBuffer {
    config;
    httpClient;
    buffer = [];
    flushTimer = null;
    isFlushing = false;
    constructor(config, httpClient) {
        this.config = config;
        this.httpClient = httpClient;
        log.debug('WriteBuffer initialized', config);
    }
    /**
     * Add operation to buffer
     */
    add(operation) {
        this.buffer.push(operation);
        log.debug('Operation added to buffer', {
            type: operation.type,
            bufferSize: this.buffer.length,
        });
        // Auto-flush after delay if not already scheduled
        if (!this.flushTimer && !this.isFlushing) {
            this.flushTimer = setTimeout(() => this.flush(), this.config.maxBatchDelay);
        }
        // Immediate flush if buffer full
        if (this.buffer.length >= this.config.maxBatchSize) {
            this.flush();
        }
    }
    /**
     * Flush buffer to server
     */
    async flush() {
        if (this.buffer.length === 0) {
            log.debug('Buffer is empty, nothing to flush');
            return;
        }
        if (this.isFlushing) {
            log.debug('Flush already in progress');
            return;
        }
        this.isFlushing = true;
        // Clear flush timer
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        // Take all operations from buffer
        const batch = this.buffer.splice(0, this.buffer.length);
        try {
            log.debug('Flushing write buffer', { count: batch.length });
            // Serialize dates before sending
            const serializedOps = batch.map(op => ({
                ...op,
                data: serializeDates(op.data),
            }));
            await this.httpClient.post('/api/batch', { operations: serializedOps });
            log.debug('Write buffer flushed successfully', { count: batch.length });
        }
        catch (error) {
            log.error('Failed to flush write buffer - operations will be retried', {
                error: error instanceof Error ? error.message : String(error),
                count: batch.length,
            });
            // Re-add to buffer for retry
            this.buffer.unshift(...batch);
            // Don't throw - let the client continue
            // Operations will be retried on next flush
        }
        finally {
            this.isFlushing = false;
        }
    }
    /**
     * Get current buffer size
     */
    size() {
        return this.buffer.length;
    }
    /**
     * Close and flush buffer
     */
    async close() {
        if (this.flushTimer) {
            clearTimeout(this.flushTimer);
            this.flushTimer = null;
        }
        // Final flush
        await this.flush();
    }
}
//# sourceMappingURL=WriteBuffer.js.map